import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { 
  Star, MapPin, Phone, Mail, Clock, Check, ChevronLeft, 
  Image as ImageIcon, MessageSquare, Calendar as CalendarIcon 
} from 'lucide-react';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function SalonDetail() {
  const { salonId } = useParams();
  const navigate = useNavigate();
  const { theme, setMenTheme, setWomenTheme } = useTheme();
  const { user, getAuthHeader } = useAuth();

  const [salon, setSalon] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('services');

  // Booking state
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingForm, setBookingForm] = useState({ name: '', phone: '', notes: '' });
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);

  useEffect(() => {
    fetchSalon();
    fetchReviews();
  }, [salonId]);

  useEffect(() => {
    if (salon) {
      if (salon.category === 'men') {
        setMenTheme();
      } else {
        setWomenTheme();
      }
    }
  }, [salon]);

  useEffect(() => {
    if (selectedDate && salonId) {
      fetchAvailableSlots();
    }
  }, [selectedDate, salonId]);

  useEffect(() => {
    if (user) {
      setBookingForm(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        phone: prev.phone || user.phone || ''
      }));
    }
  }, [user]);

  const fetchSalon = async () => {
    try {
      const response = await fetch(`${API}/salons/${salonId}`);
      if (response.ok) {
        const data = await response.json();
        setSalon(data);
      } else {
        toast.error('Salon not found');
        navigate('/salons');
      }
    } catch (error) {
      console.error('Failed to fetch salon:', error);
      toast.error('Failed to load salon details');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API}/salons/${salonId}/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    setSelectedSlot(null);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await fetch(`${API}/salons/${salonId}/available-slots?date=${dateStr}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSlots(data.slots || []);
      }
    } catch (error) {
      console.error('Failed to fetch slots:', error);
      setAvailableSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();

  // Service check
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

  // Date check
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

  // Slot check
    if (!selectedSlot) {
      toast.error('Please select a time slot');
      return;
    }

  // Name check
    if (!bookingForm.name.trim()) {
      toast.error('Please enter your name');
      return;
    }

  // Phone check
    if (!bookingForm.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

  // Exact 10 digit phone check
    if (!/^\d+$/.test(bookingForm.phone.trim())) {
      toast.error('Phone number must contain only digits');
      return;
    }

    if (!/^\d{10}$/.test(bookingForm.phone.trim())) {
      toast.error('Phone number must be exactly 10 digits');
      return;
    }

    setBookingLoading(true);

    try {
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          salon_id: salonId,
          service_id: selectedService.service_id,
          booking_date: format(selectedDate, 'yyyy-MM-dd'),
          time_slot: selectedSlot,
          customer_name: bookingForm.name.trim(),
          customer_phone: bookingForm.phone.trim(),
          notes: bookingForm.notes.trim()
        })
      });

      let data ={};
      try{
        data = await response.json();
      } catch{
        data = {};
      }

      
      if(response.ok) {
        toast.success('Booking confirmed!');
        navigate(`/booking-confirmation/${data.booking_id}`);
      } else {
        toast.error(
          typeof data.detail === 'string'? data.detail : 'slot is already booked'
        );
        return;
      }

    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.message || 'Something went wrong.Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!salon) return null;

  const isMen = salon.category === 'men';
  const bgClass = isMen ? 'bg-zinc-950' : 'bg-rose-50';
  const textClass = isMen ? 'text-white' : 'text-rose-900';
  const secondaryTextClass = isMen ? 'text-zinc-400' : 'text-rose-600';
  const cardBgClass = isMen ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-pink-100';
  const accentClass = isMen ? 'text-blue-500' : 'text-pink-600';
  const buttonClass = isMen 
    ? 'bg-blue-600 hover:bg-blue-700 text-white rounded-none uppercase tracking-wider font-bold'
    : 'bg-pink-600 hover:bg-pink-700 text-white rounded-full font-serif italic';

  return (
    <div className={`min-h-screen ${bgClass}`}>
      <Navbar />

      {/* Hero Banner */}
      <div className="relative h-[50vh] min-h-[400px]">
        <img 
          src={salon.banner || 'https://images.unsplash.com/photo-1579643046891-3386be56709c?crop=entropy&cs=srgb&fm=jpg&q=85'} 
          alt={salon.name}
          className="w-full h-full object-cover"
        />
        <div className={`absolute inset-0 ${isMen ? 'bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent' : 'bg-gradient-to-t from-rose-50 via-rose-50/50 to-transparent'}`} />
        
        {/* Back Button */}
        <button 
          onClick={() => navigate(-1)}
          className={`absolute top-20 left-4 p-2 rounded-full ${isMen ? 'bg-zinc-900/80 text-white' : 'bg-white/80 text-rose-900'} backdrop-blur-sm`}
          data-testid="back-btn"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Salon Info Card */}
            <Card className={cardBgClass} data-testid="salon-info">
              <CardContent className="p-8">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
                  <div>
                    <Badge className={isMen ? 'bg-blue-600/20 text-blue-400 border-0' : 'bg-pink-600/20 text-pink-600 border-0'}>
                      {salon.category === 'men' ? "Men's Salon" : "Women's Salon"}
                    </Badge>
                    {salon.is_verified && (
                      <Badge className="ml-2 bg-green-600/20 text-green-500 border-0">
                        <Check className="w-3 h-3 mr-1" /> Verified
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                    <span className={`font-bold ${textClass}`}>{salon.rating?.toFixed(1) || '0.0'}</span>
                    <span className={secondaryTextClass}>({salon.review_count || 0} reviews)</span>
                  </div>
                </div>

                <h1 className={`text-3xl md:text-4xl font-bold ${textClass} ${isMen ? 'font-heading-men uppercase tracking-wider' : 'font-heading-women italic'} mb-4`}>
                  {salon.name}
                </h1>

                <p className={`${secondaryTextClass} mb-6 leading-relaxed`}>
                  {salon.description || 'Premium grooming and beauty services'}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <MapPin className={`w-5 h-5 ${accentClass}`} />
                    <span className={secondaryTextClass}>{salon.address}, {salon.city}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className={`w-5 h-5 ${accentClass}`} />
                    <span className={secondaryTextClass}>{salon.phone}</span>
                  </div>
                  {salon.email && (
                    <div className="flex items-center gap-3">
                      <Mail className={`w-5 h-5 ${accentClass}`} />
                      <span className={secondaryTextClass}>{salon.email}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Clock className={`w-5 h-5 ${accentClass}`} />
                    <span className={secondaryTextClass}>Open Today</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={`w-full grid grid-cols-3 ${isMen ? 'bg-zinc-900' : 'bg-white'}`}>
                <TabsTrigger value="services" data-testid="tab-services">Services</TabsTrigger>
                <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
                <TabsTrigger value="reviews" data-testid="tab-reviews">Reviews</TabsTrigger>
              </TabsList>

              <TabsContent value="services" className="mt-6">
                <div className="grid gap-4" data-testid="services-list">
                  {salon.services?.filter(s => s.is_active).map((service) => (
                    <Card 
                      key={service.service_id}
                      className={`${cardBgClass} cursor-pointer transition-all ${selectedService?.service_id === service.service_id ? (isMen ? 'ring-2 ring-blue-500' : 'ring-2 ring-pink-500') : ''}`}
                      onClick={() => setSelectedService(selectedService?.service_id === service.service_id ? null :service)}
                      data-testid={`service-${service.service_id}`}
                    >
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className={`font-semibold ${textClass}`}>{service.name}</h3>
                          <p className={`text-sm ${secondaryTextClass}`}>{service.description}</p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className={`text-sm ${secondaryTextClass}`}>
                              <Clock className="w-4 h-4 inline mr-1" />
                              {service.duration} min
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-2xl font-bold ${accentClass}`}>${service.price}</p>
                          {selectedService?.service_id === service.service_id && (
                            <Check className={`w-5 h-5 ${accentClass} ml-auto mt-1`} />
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="mt-6">
                {salon.gallery?.length > 0 ? (
                  <div className="gallery-grid">
                    {salon.gallery.map((img, i) => (
                      <div key={i} className={`aspect-square rounded-lg overflow-hidden ${isMen ? '' : 'rounded-2xl'}`}>
                        <img src={img} alt={`Gallery ${i+1}`} className="w-full h-full object-cover" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-12 ${secondaryTextClass}`}>
                    <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No gallery images available</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="reviews" className="mt-6">
                {reviews.length > 0 ? (
                  <div className="space-y-4" data-testid="reviews-list">
                    {reviews.map((review) => (
                      <Card key={review.review_id} className={cardBgClass}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className={`font-medium ${textClass}`}>{review.user_name}</span>
                            <div className="flex items-center gap-1">
                              {[...Array(5)].map((_, i) => (
                                <Star 
                                  key={i} 
                                  className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'}`} 
                                />
                              ))}
                            </div>
                          </div>
                          {review.comment && (
                            <p className={secondaryTextClass}>{review.comment}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className={`text-center py-12 ${secondaryTextClass}`}>
                    <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No reviews yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Booking Widget */}
          <div className="lg:col-span-1">
            <div className={`booking-widget ${cardBgClass} rounded-xl border p-6`} data-testid="booking-widget">
              <h2 className={`text-xl font-bold ${textClass} mb-6 ${isMen ? 'font-heading-men uppercase tracking-wider' : 'font-heading-women italic'}`}>
                Book Appointment
              </h2>

              <form onSubmit={handleBooking} className="space-y-6">
                {/* Selected Service */}
                {selectedService ? (
                  <div className={`p-3 rounded-lg ${isMen ? 'bg-zinc-800' : 'bg-pink-50'}`}>
                    <div className="flex justify-between items-center">
                      <span className={textClass}>{selectedService.name}</span>
                      <span className={`font-bold ${accentClass}`}>${selectedService.price}</span>
                    </div>
                  </div>
                ) : (
                  <p className={`text-sm ${secondaryTextClass}`}>Select a service from the list</p>
                )}

                {/* Date Picker */}
                <div>
                  <label className={`block text-sm font-medium ${textClass} mb-2`}>
                    <CalendarIcon className="w-4 h-4 inline mr-2" />
                    Select Date
                  </label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date() || date > new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)}
                    className={`rounded-md border ${isMen ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-pink-200'}`}
                    data-testid="date-picker"
                  />
                </div>

                {/* Time Slots */}
                {selectedDate && (
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-2`}>Select Time</label>
                    {slotsLoading ? (
                      <div className="grid grid-cols-3 gap-2">
                        {[1,2,3,4,5,6].map(i => (
                          <div key={i} className={`h-10 rounded animate-pulse ${isMen ? 'bg-zinc-800' : 'bg-pink-100'}`} />
                        ))}
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2" data-testid="time-slots">
                        {availableSlots.map((slot) => (
                          <button
                            key={slot.time}
                            type="button"
                            onClick={() => setSelectedSlot(slot.time)}
                            className={`time-slot p-2 text-sm rounded border ${
                              selectedSlot === slot.time
                                ? (isMen ? 'bg-blue-600 text-white border-blue-600 selected' : 'bg-pink-600 text-white border-pink-600 selected')
                                : (isMen ? 'bg-zinc-800 text-zinc-300 border-zinc-700 hover:border-blue-500' : 'bg-white text-rose-900 border-pink-200 hover:border-pink-500')
                            }`}
                            data-testid={`slot-${slot.time}`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className={`text-sm ${secondaryTextClass}`}>No slots available for this date</p>
                    )}
                  </div>
                )}

                {/* Customer Details */}
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-1`}>Your Name</label>
                    <Input
                      type="text"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                      placeholder="Enter your name"
                      className={isMen ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-pink-200'}
                      required
                      data-testid="booking-name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-1`}>Phone Number</label>
                    <Input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      placeholder="Enter phone number"
                      className={isMen ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-pink-200'}
                      required
                      data-testid="booking-phone"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${textClass} mb-1`}>Notes (Optional)</label>
                    <Textarea
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({...bookingForm, notes: e.target.value})}
                      placeholder="Any special requests?"
                      className={isMen ? 'bg-zinc-800 border-zinc-700 text-white' : 'bg-white border-pink-200'}
                      rows={3}
                      data-testid="booking-notes"
                    />
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  className={`w-full py-6 ${buttonClass}`}
                  disabled={!selectedService || !selectedDate || !selectedSlot || bookingLoading}
                  data-testid="book-now-btn"
                >
                  {bookingLoading ? 'Booking...' : 'Book Now'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
